import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { green, lightBlue } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import parse from "html-react-parser";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { skipToken } from "@reduxjs/toolkit/query";
import { messagingApi, useGetForumSubscriberInfoQuery } from "messaging/_api/messaging-api";
import MessageHistoryControl from "messaging/_views/components/message-history-dialog";
import MessageMarkdownContent from "_shared/_views/markdown/message-markdown-content";
import EditedMark from "messaging/correspondence/_views/edited-mark";
import { MessageActionButton } from "messaging/correspondence/_views/message-actions";
import { BubbleWithheldText, MessageBubble } from "messaging/correspondence/_views/message-bubble";
import BubbleTimestamp from "messaging/correspondence/_views/bubble-timestamp";
import MessageEditField from "messaging/correspondence/_views/message-edit-field";
import { ReadStateButton } from "messaging/correspondence/_views/read-state-controls";
import { SystemNotice } from "messaging/correspondence/_views/system-notice";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import {
  addComment,
  deleteComment,
  editComment,
  publishComment,
  unpublishComment,
} from "work/proposals/comments/_store/comments-redux-slice";
import type { SeenCommentWatcher } from "work/proposals/comments/_view/use-read-comments-when-seen";
import { conversationMomentOf } from "work/proposals/comments/_view/proposal-comment-list";
import Comment from "work/proposals/comments/comment";
import { addCommentThread } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";


const NameLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "disabled" && prop !== "hasLeft",
})<{ disabled: boolean; hasLeft?: boolean }>(({ theme, disabled, hasLeft }) => ({
  display: "inline-block",
  cursor: disabled ? "default" : "pointer",
  textDecoration: disabled ? "none" : "underline",
  ...(hasLeft && { color: theme.palette.text.secondary }),
}));
const AvatarButton = styled(IconButton)(() => ({
  padding: 0,
}));
/** Says a comment has not reached its audience yet: an author-only draft, or one still on the wire. */
const PendingMark = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.warning.dark,
  fontStyle: "italic",
}));
const CommentContent = styled("span")(({ theme }) => ({
  boxSizing: "border-box",
  display: "block",
  whiteSpace: "normal",
  width: "100%",
  "& span.action": {
    fontWeight: 500,
    "&.accepted": {
      color: theme.palette.success.main,
    },
    "&.rejected": {
      color: theme.palette.error.main,
    },
  },
  "& > span.comment-redline": {
    width: "100%",
    "& > span.change-group": {
      whiteSpace: "nowrap",
      "& .added": {
        "&.resolved": {
          color: theme.palette.text.primary,
        },
        backgroundColor: "unset",
        color: theme.palette.text.primary,
      },
    },
    "& span.added": {
      backgroundColor: green[300],
      color: theme.palette.text.primary,
      "&.resolved": {
        backgroundColor: "unset",
        color: lightBlue[700],
      },
    },
    "& span.removed": {
      textDecoration: "line-through",
      textDecorationColor: theme.palette.error.main,
      "&.resolved": {
        textDecorationColor: lightBlue[700],
      },
    },
    "& span.unchanged": {
      color: theme.palette.text.primary,
    },
  },
}));

/**
 * What a row's audience switch is drawn against: the comment as the row's open edit currently aims it,
 * and how to record the author aiming it somewhere else.
 *
 * The row hands these out rather than being handed a finished element, because the audience on offer
 * belongs to an edit only the row holds. The surfaces that supply the switch own the `Proposal` it
 * needs and nothing else.
 */
export type CommentAudienceSwitchSlot = {
  comment: Comment;
  onCommentReaimed: (comment: Comment) => void;
};

/**
 * The unsaved edit a row is holding: the comment as its author has re-aimed it, and the text they have
 * rewritten. Neither is anyone else's until they save, so both live here rather than in the store.
 *
 * The draft used to be the shown comment's own `CommentThread`, written in place by the audience
 * switch. A comment and its thread are immutable — every change returns a new object — so that write
 * was invisible to React, and, because one thread instance is shared by every comment in the
 * conversation, it could re-aim rows the author never touched.
 */
type OpenCommentEdit = {
  draft: Comment;
  text: string;
};

type CommentItemProps = {
  className?: string;
  comment: Comment;
  /** Omitted by a read-only surface, which offers no audience choice at all. */
  renderAudienceSwitch?: (slot: CommentAudienceSwitchSlot) => React.ReactNode;
  readOnly?: boolean;
  /**
   * Scrolls the row into view as it appears, or as this turns true — how the unread-comments stop of
   * "Prev / Next" opens the pane on the oldest unread comment rather than wherever it was last left.
   */
  scrollIntoView?: boolean;
  /**
   * Marks the comment read once the reader has scrolled it into view (#5824). The pane hands every row
   * of the conversation it lists the same watcher; a surface that marks nothing read omits it.
   */
  readWhenSeen?: SeenCommentWatcher;
};

const userIdRegEx = new RegExp(
  /userId:\s?([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})/
);

/**
 * When a comment was written, as its row's caption says it. A bare date means published, the way a
 * conversation is read; only an edit names itself. The year is left out for this year's comments, which
 * is nearly all of them, and kept for anything older so the date cannot be mistaken for a recent one.
 */
export function commentCaptionDate(date: AHDate, now: AHDate = new AHDate()): string {
  return date.format(date.year === now.year ? "MMM D, h:mm A" : "MMM D, YYYY, h:mm A");
}

/**
 * Brings the row into view in its own list, and moves nothing else.
 *
 * Not `row.scrollIntoView`: that scrolls every scrollable ancestor, and an element with
 * `overflow: hidden` is one. The page clips the pane in a column of its own, so a traverser stop
 * scrolled that column 34px sideways and slid the pane under the proposal, cutting off its left edge
 * until the page reloaded (#5821, #5824). `proposal-correspondence` scrolls to the list's foot the same
 * way, for the same reason.
 *
 * "Nearest", as `scrollIntoView` put it: a row above the list's top lands at the top edge, with the newer
 * comments below it still in view; a row below lands at the bottom edge unless it is taller than the
 * list; a row already in view stays put.
 */
function scrollRowIntoList(row: HTMLElement): void {
  const list = nearestVerticalScroller(row);
  if (!list) return;
  const rowBox = row.getBoundingClientRect();
  const listBox = list.getBoundingClientRect();
  let offset = 0;
  if (rowBox.top < listBox.top) offset = rowBox.top - listBox.top;
  else if (rowBox.bottom > listBox.bottom) offset = Math.min(rowBox.bottom - listBox.bottom, rowBox.top - listBox.top);
  if (offset !== 0) list.scrollBy?.({ top: offset, behavior: "smooth" });
}

function nearestVerticalScroller(element: HTMLElement): HTMLElement | undefined {
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    const { overflowY } = getComputedStyle(ancestor);
    if (overflowY === "auto" || overflowY === "scroll") return ancestor;
  }
  return undefined;
}

export default function CommentItem(props: Readonly<CommentItemProps>) {
  const { className, comment, renderAudienceSwitch } = props;
  const readOnly = props.readOnly ?? false;
  const scrollIntoView = props.scrollIntoView ?? false;
  const { readWhenSeen } = props;
  const commentId = comment.id?.value;

  // Hands the row to the seen-watcher as it mounts, and stops the watching when it goes. Nothing but
  // the row and its comment is a dependency: a callback that changed with the scroll request handed the
  // same on-screen row over again as though it had just arrived, and read a comment the reader had
  // marked unread without its row ever leaving the screen.
  const rowRef = React.useRef<HTMLElement | null>(null);
  const attachRow = React.useCallback(
    (row: HTMLElement | null) => {
      if (!row) return;
      rowRef.current = row;
      const stopWatching = commentId && readWhenSeen ? readWhenSeen.watch(row, commentId) : undefined;
      return () => {
        rowRef.current = null;
        stopWatching?.();
      };
    },
    [commentId, readWhenSeen],
  );
  // Scrolls as the row mounts with the request on, and as the request turns on for a row already drawn:
  // the two moments the stop asks for it. Not when the row merely renders again.
  React.useEffect(() => {
    if (scrollIntoView && rowRef.current) scrollRowIntoList(rowRef.current);
  }, [scrollIntoView]);

  const [openEdit, setOpenEdit] = React.useState<OpenCommentEdit | null>(null);
  const [isUpdatingReadStatus, setIsUpdatingReadStatus] = React.useState(false);
  const [isChangingPublishState, setIsChangingPublishState] = React.useState(false);
  // Every comment action writes the comment as it is performed, so each one owns an in-flight flag: the
  // row's controls are disabled while its own write is on the wire rather than while a proposal saves.
  const [isPersistingEdit, setIsPersistingEdit] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const commenter = useSelector(getIndividualByUserId(comment.senderId ?? undefined));

  const { openDialog } = useDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const commentUserId: Guid | undefined = useMemo(() => {
    const userIdMatch = userIdRegEx.exec(comment.content);
    if (!userIdMatch) return;
    return new Guid(userIdMatch[1]);
  }, [comment.content]);
  const commentUser = useSelector(getIndividualByUserId(commentUserId));

  const individualUserIds = new Set([
    ...(comment.senderId ? [comment.senderId.value] : []),
    ...(commentUserId ? [commentUserId.value] : []),
  ]);
  useGetIndividualsByUserIdsQuery({ userIds: [...individualUserIds] }, { skip: individualUserIds.size === 0 });

  // The directory may no longer name an author who has left the proposal, and cannot say they left.
  // The comment's forum still names everyone who wrote in it, marked when they have left (#6878).
  const threadForumId = comment.thread.id?.value;
  const isOthersComment = Boolean(comment.senderId) && !comment.senderId?.isEqualTo(session.user?.id);
  const { data: forumParticipants } = useGetForumSubscriberInfoQuery(
    threadForumId && isOthersComment ? { forumId: threadForumId } : skipToken,
  );
  const author = forumParticipants?.find((participant) => participant.individual.userId.isEqualTo(comment.senderId));
  const authorName = author?.displayName ?? commenter?.fullName;

  function getCommentContent(comment: Comment) {
    // A withheld comment keeps its place in the thread rather than vanishing out of it, so the row still
    // renders — as a tombstone. There is nothing to fall back to if these branches are missed: the
    // server hydrates a withheld comment from a type with no body, so `content` is empty by the time it
    // is here. Attachments are indicated and never presented; the tombstone carries only how many there
    // were.
    if (comment.isDeleted) {
      return (
        <BubbleWithheldText data-testid="deleted-comment-tombstone">
          This comment was deleted
          {comment.attachmentCount > 0 && (
            <span data-testid="deleted-comment-attachment-count">
              {` (${comment.attachmentCount} attachment${comment.attachmentCount === 1 ? "" : "s"})`}
            </span>
          )}
        </BubbleWithheldText>
      );
    }

    // A retraction used to be a disappearance: the comment left the audience's screen with nothing said
    // about it. It leaves this instead, in the place the comment held — `published` is write-once, so
    // the row has not moved — telling the reader that what they had read was taken back, and when.
    if (comment.isRetractedTombstone) {
      return (
        <BubbleWithheldText data-testid="retracted-comment-tombstone">
          {`Removed at ${comment.retracted?.format("MMM D, YYYY h:mm A") ?? ""}`}
          {comment.attachmentCount > 0 && (
            <span data-testid="retracted-comment-attachment-count">
              {` (${comment.attachmentCount} attachment${comment.attachmentCount === 1 ? "" : "s"})`}
            </span>
          )}
        </BubbleWithheldText>
      );
    }

    let content: string = comment.content;

    if (commentUser && comment.content) {
      content = content.replace(userIdRegEx, commentUser.fullName);
    }
    // A proposal's comments carry text only (#5819): a file posted before that is not drawn. A comment
    // that was nothing but a file says so, rather than standing as an empty bubble.
    if (!content && comment.files.length > 0) {
      return <BubbleWithheldText data-testid="comment-files-withheld">Files are not shown on proposal comments</BubbleWithheldText>;
    }
    return (
      content && (
        <CommentContent>
          {/* A comment written in the composer is markdown, as a chat message is. Older comments
              carry the markup their redline notes were written in. */}
          {/<[a-z][\s\S]*>/i.test(content) ? parse(content) : <MessageMarkdownContent content={content} />}
        </CommentContent>
      )
    );
  }

  function handleViewCommenterProfile() {
    if (!commenter) {
      console.warn("Commenter not found");
      return;
    }

    openDialog({
      component: <ViewIndividualProfile profile={commenter.profile} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  const isOwnComment = Boolean(comment.senderId?.isEqualTo(session.user?.id));
  const isRemoved = comment.isDeleted || Boolean(comment.retracted);
  const historyLabel = comment.isDeleted ? "Deleted" : comment.retracted ? "Removed" : comment.updated ? "Edited" : undefined;
  // Removed-message history is visible only to the author. Everyone else already has the tombstone in
  // the comment body, so repeating Deleted or Removed beside the timestamp adds no useful information.
  // The message-history endpoint enforces the same author boundary.
  const canShowHistoryStatus = Boolean(
    historyLabel &&
      comment.senderId &&
      comment.id &&
      !comment.markedForCreation &&
      (!isRemoved || isOwnComment),
  );
  const canViewHistory = Boolean(
    canShowHistoryStatus &&
      comment.thread.id,
  );
  const messageForHistory = canShowHistoryStatus
    ? comment.toMessage({ isViewingAsVendor: session.context?.viewingAsVendor ?? false })
    : undefined;
  // A tombstone is never unread. You cannot have unread content that no longer exists, and the server
  // agrees: a deleted message is excluded from `Forum.NumUnreadMessages`, so treating one as unread here
  // would put a dot on a row that no badge counts and offer a Mark Read that writes to a deleted row.
  // `Comment.isUnreadBy` states that rule once, so this row and the pane's Mark all read agree.
  const isUnread = Boolean(
    !comment.isDeleted &&
      !comment.isRetractedTombstone &&
      ((!isOwnComment && comment.isUnreadBy(session.user?.id)) || comment.markedForUnread)
  );
  // Nor is it pending: whatever was outstanding about the comment stopped mattering when it was
  // deleted. `Comment.isPending` says so itself, so the row and the comment button's badge cannot
  // disagree about whether a tombstone is undelivered work.
  const showPendingIndicator = comment.isPending;
  async function handleReadStatusChanged(updatedComment: Comment) {
    if (!updatedComment.id || isUpdatingReadStatus) return;

    setIsUpdatingReadStatus(true);

    // Hoisted out of the try block: the React Compiler cannot lower optional chaining, `??` or a
    // ternary inside try/catch, and bails on the whole component when it finds one.
    const isViewingAsVendor = session.context?.viewingAsVendor ?? false;
    const failureMessage = readOnly ? "Failed to mark comment as read." : "Failed to update comment read status.";
    const errorMessage = readOnly ? "Error marking comment as read:" : "Error updating comment read status:";

    try {
      const returnedMessages = await dispatch(
        messagingApi.endpoints.createBulkMessages.initiate({
          messages: [
            updatedComment.toMessage({
              isViewingAsVendor,
            }),
          ],
        }),
      ).unwrap();
      if (returnedMessages.length < 1) {
        console.error(failureMessage);
      } else {
        const returnedComment = Comment.fromMessage(returnedMessages[0]);
        dispatch(addComment(returnedComment));
        dispatch(addCommentThread(returnedComment.thread));
      }
    } catch (error) {
      console.error(errorMessage, error);
    }

    // Ran as a `finally` clause until the React Compiler, which cannot lower one, started skipping
    // this component. The early return above became an `else` so this is still reached on every path.
    setIsUpdatingReadStatus(false);
  }

  /** The reader asked for it unread: the pane does not read it again while its row stays on screen. */
  function markUnreadByHand(): Comment {
    readWhenSeen?.holdUnread(commentId);
    return comment.markForUnread();
  }

  function handleCommentItemClicked() {
    if (!isUnread || isUpdatingReadStatus) return;

    void handleReadStatusChanged(comment.markForReadReceipt());
  }

  async function handlePublishStateChanged(publish: boolean) {
    if (isChangingPublishState) return;

    setIsChangingPublishState(true);

    // Hoisted for the same reason as in `handleReadStatusChanged` above.
    const publishAction = publish ? publishComment : unpublishComment;
    const publishErrorMessage = publish ? "Error publishing comment:" : "Error unpublishing comment:";
    const publishFailure = publish
      ? { doing: "publishing your comment", operation: "proposal.comments.publish" }
      : { doing: "retracting your comment", operation: "proposal.comments.retract" };

    try {
      await dispatch(publishAction({ session, comment })).unwrap();
    } catch (error) {
      console.error(publishErrorMessage, error);
      showErrorSnackbar(error, publishFailure);
    }

    setIsChangingPublishState(false);
  }

  /**
   * Commits the row's open edit — the author's Save for this comment, and the only thing that commits it.
   *
   * The edit is written here rather than staged for a proposal save: a comment's audience is reading the
   * old text until the write lands, and making them wait on a save the author may never perform is what
   * tied the conversation to the negotiation's own Save button. The draft is what goes on the wire, so
   * the audience the author re-aimed the row at travels with their new text — and cancelling instead
   * abandons both.
   */
  async function handleEditSaved(edit: OpenCommentEdit) {
    if (edit.text.trim() === "" || isPersistingEdit) return;

    setIsPersistingEdit(true);
    try {
      await dispatch(editComment({ session, comment: edit.draft.edit(edit.text) })).unwrap();
      setOpenEdit(null);
    } catch (error) {
      console.error("Error saving comment edit:", error);
      // The row stays open on failure: the author's text — and their audience choice — are still theirs
      // to retry or abandon rather than silently lost.
      showErrorSnackbar(error, { doing: "saving your comment", operation: "proposal.comments.edit" });
    }

    // Ran as a `finally` clause until the React Compiler, which cannot lower one, started skipping
    // this component. Neither branch above returns early, so this is still reached on every path.
    setIsPersistingEdit(false);
  }

  /**
   * Deletes the comment outright.
   *
   * It used to be staged behind a "Pending Deletion" chip that only a proposal save resolved, so a
   * comment its author had deleted stayed readable by its audience until — and unless — they saved the
   * proposal. Pressing Delete deletes. There is deliberately no undo: the deletion reaches the audience
   * at once, and the server has no restore (see ROADMAP.md's open question).
   */
  async function handleCommentDeleted() {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteComment({ session, comment })).unwrap();
    } catch (error) {
      console.error("Error deleting comment:", error);
      showErrorSnackbar(error, { doing: "deleting your comment", operation: "proposal.comments.delete" });
      setIsDeleting(false);
    }
  }

  function renderReadUnreadButton(comment: Comment): React.ReactNode {
    return (
      <ReadStateButton
        unread={isUnread}
        readOnly={readOnly}
        disabled={isUpdatingReadStatus}
        onToggle={() =>
          void handleReadStatusChanged(isUnread ? comment.markForReadReceipt() : markUnreadByHand())
        }
      />
    );
  }

  const canManage = !readOnly && !comment.isDeleted && !comment.isRetractedTombstone && isOwnComment;
  const controls = (
    <>
      {!isOwnComment && renderReadUnreadButton(comment)}
      {canManage && comment.isVisibleToAudience && !comment.markedForUnpublish && !comment.markedForCreation && (
        <MessageActionButton
          label="Unpublish comment"
          icon={<UnpublishedIcon fontSize="inherit" />}
          disabled={isChangingPublishState}
          onClick={() => void handlePublishStateChanged(false)}
        />
      )}
      {canManage && (
        <MessageActionButton
          label="Edit comment"
          icon={<EditIcon fontSize="inherit" />}
          onClick={() => setOpenEdit({ draft: comment, text: comment.content ?? "" })}
        />
      )}
      {canManage && (
        <MessageActionButton
          label="Delete comment"
          icon={<DeleteIcon fontSize="inherit" />}
          destructive
          disabled={isDeleting}
          onClick={() => void handleCommentDeleted()}
        />
      )}
    </>
  );

  const avatar = commenter?.isVisible ? (
    <Tooltip title="View profile">
      <AvatarButton
        aria-label={`View ${commenter.fullName}'s profile`}
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleViewCommenterProfile();
        }}
      >
        <IndividualAvatar individual={commenter} />
      </AvatarButton>
    </Tooltip>
  ) : (
    <IndividualAvatar individual={commenter ?? author?.individual} />
  );
  // The reader's own comment is named by its side of the pane, as a sent chat message is.
  const senderName = isOwnComment ? undefined : (
    <NameLink
      className="comment-author-name"
      // The info row cuts a long name short; the whole of it shows under the pointer.
      title={authorName}
      disabled={!commenter?.isVisible}
      hasLeft={author?.hasLeft}
      data-author-has-left={author?.hasLeft || undefined}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!commenter?.isVisible) return;
        handleViewCommenterProfile();
      }}
    >
      {authorName}
    </NameLink>
  );
  const status = (
    <>
      {/* A draft's one way out, so it stays in sight beside "Pending" rather than waiting for the hover. */}
      {canManage && comment.isUnpublishedDraft && (
        <MessageActionButton
          label="Publish comment"
          icon={<SendIcon fontSize="inherit" />}
          disabled={isChangingPublishState}
          onClick={() => void handlePublishStateChanged(true)}
        />
      )}
      {showPendingIndicator && (
        <PendingMark data-testid="comment-status-chip">
          <span data-testid="comment-status-labels">{getPendingStatus()}</span>
        </PendingMark>
      )}
      {!comment.isRetractedTombstone && <EditedMark message={comment as never} />}
      {/*
        * The control is a sibling of the time, not a child of it, so a reader hears "History" and the
        * time as two things. See #4750 and #4806/#4807.
        */}
      {messageForHistory && historyLabel && (
        <MessageHistoryControl
          message={messageForHistory}
          label={historyLabel}
          canViewHistory={canViewHistory}
          surface="proposalComment"
        />
      )}
    </>
  );

  const commentListItem = (
    <MessageBubble
      ref={attachRow}
      className={className}
      side={isOwnComment ? "sent" : "received"}
      avatar={avatar}
      senderName={senderName}
      time={
        !comment.isRetractedTombstone && (
          <BubbleTimestamp
            data-testid="comment-date"
            className="comment-date"
            // The moment the list sorts and divides days on, so a draft's time sits under its own day.
            date={conversationMomentOf(comment)}
          />
        )
      }
      status={status}
      controls={!openEdit && controls}
      unread={isUnread}
      onMarkRead={handleCommentItemClicked}
      dimmed={comment.isPending}
      data-testid="comment-list-item"
    >
      {!readOnly && openEdit ? (
        <MessageEditField
          text={openEdit.text}
          saving={isPersistingEdit}
          // The switch is drawn against the draft, not the shown comment, so re-aiming it is a change
          // to this edit and nothing else until the author saves.
          helperText={renderAudienceSwitch?.({
            comment: openEdit.draft,
            onCommentReaimed: (reaimed) => setOpenEdit({ ...openEdit, draft: reaimed }),
          })}
          onChange={(text) => setOpenEdit({ ...openEdit, text })}
          onSave={() => void handleEditSaved(openEdit)}
          onCancel={() => setOpenEdit(null)}
        />
      ) : (
        getCommentContent(comment)
      )}
    </MessageBubble>
  );
  return comment.senderId ? (
    commentListItem
  ) : (
    <SystemNotice
      ref={attachRow}
      published={comment.published}
      // A read-only pane marks a notice read by a press on it; it offers no button.
      readState={!readOnly && renderReadUnreadButton(comment)}
      unread={isUnread}
      onMarkRead={handleCommentItemClicked}
    >
      <ReactMarkdown>{comment.content}</ReactMarkdown>
    </SystemNotice>
  );
}

/**
 * What a comment's pending chip says.
 *
 * There is only one thing it can mean now: a comment that has not reached its audience — an author-only
 * draft, or one still on the wire. Edits and deletions used to be pending too, held in the store behind
 * a chip until a proposal save sent them; each of those writes when its author performs it, so neither
 * ever waits for anything a reader could be told about.
 */
function getPendingStatus(): string {
  return "Pending";
}
